import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Papa from 'papaparse';
import { Icon } from 'antd';
import { getPage, setPage } from './lib/url';
import { buildDataSourceFrom, ACTIONS, PAGES_URL, IMAGES_URL } from './lib/data';
import { buildImageIndex } from './lib/images';
import { isBrowser, isTablet, BrowserView, TabletView, MobileOnlyView } from 'react-device-detect';
import Page from './Page';
import Header from './Header';
import Navigation from './Navigation';
import SelectedState from './SelectedState';
import './App.css';

export default class App extends Component {
  vh = 1
  previousPage = 0
  page = 1
  maxPage = 1
  dataSource = []

  /*
   *  the pageData object queues up previous, current,
   *  and next pages of data so that the previous and next views
   *  can be rendered before the user requests them
   */
  numPagesToPreload = 10
  pageData = []

  state = {
    showNav: false,
    updatedAt: new Date().getTime(),
  }

  handleToggleNavVisibility = () => {
    this.setState({ showNav: !this.state.showNav });
  }

  componentDidMount() {
    this.assignViewHeightCustomProperty();
    window.addEventListener("resize", this.handleOrientationChange.bind(this));

    window.onhashchange = this.handleHashChange.bind(this);
    this.page = getPage();
    this.trackPageView(this.page);
    
    Papa.parse(PAGES_URL, {
      download: true,
      header: true,
      complete: this.onPagesLoaded.bind(this)
    })
    
    Papa.parse(IMAGES_URL, {
      download: true,
      header: true,
      complete: this.onImagesLoaded.bind(this)
    })
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleOrientationChange.bind(this));
  }

  trackPageView(pageNumber) {
    if (!!window && !!window.analytics) {
      window.analytics.page({pageNumber});
    } else {
      console.warn("trackPageView() failed: window.analytics does not exist");
    }
  }

  assignViewHeightCustomProperty() {
    // convert viewport height to CSS vh units
    this.vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${this.vh}px`);
    this.setState({ updatedAt: new Date().getTime() });
  }

  handleOrientationChange() {
    this.checkWindowHeightUntilItChanges();
  }

  checkWindowHeightUntilItChanges() {
    const intervalID = setInterval(() => {
      const newVH = window.innerHeight * 0.01;
      if (newVH !== this.vh) {
        clearInterval(intervalID);
        this.assignViewHeightCustomProperty();
      }
    }, 50);
  }

  onPagesLoaded = (response) => {
    const rows = response.data
    this.dataSource = buildDataSourceFrom(rows);
    this.updatePageData();
  }

  onImagesLoaded = (response) => {
    const rows = response.data
    buildImageIndex(rows);
  }

  handleHashChange() {
    this.navigateToPage(getPage());
  }

  navigateToPage(page) {
    this.previousPage = this.page;
    this.page = page;
    this.trackPageView(page);
    this.updatePageData();
  }

  updatePageData() {
    if (this.page > this.dataSource.length) {
      this.handlePageChange(this.dataSource.length);
      return;
    }
    const currentPageIndex = this.getIndexOfCurrentPage();
    for (var i = 0; i < this.numPagesToPreload; i++) {
      const pageNumber = this.page - currentPageIndex + i;
      const pageData = this.updatePageDataAtIndex(pageNumber - 1);
      if (!!pageData) {
        this.pageData[i] = pageData;
      }
    }
    this.setState({ updatedAt: new Date().getTime() });
  }

  updatePageDataAtIndex(index) {
    if (index < 0 || index >= this.dataSource.length) {
      return null;
    }

    const pageData = this.dataSource[index];
    pageData.previousPage = this.previousPage;
    pageData.page = index + 1;
    pageData.maxPage = this.dataSource.length;
    return pageData;
  }

  getIndexOfCurrentPage() {
    return Math.ceil(this.numPagesToPreload / 2) - 1;
  }

  handlePageChange = (newPage) => {
    if (newPage <= 0) {
      newPage = 1;
    } else if (newPage > this.dataSource.length) {
      return;
    }

    setPage(newPage);
  }

  renderNavigation() {
    return (
      <Navigation
        visible={this.state.showNav}
        onToggleNavigation={this.handleToggleNavVisibility}
      />
    );
  }

  render() {
    if (this.dataSource.length === 0) {
      return (
        <div style={{ marginTop: 64, textAlign: 'center' }}>
          <Icon type="loading" />
        </div>
      );
    }

    const currentPageIndex = this.getIndexOfCurrentPage();
    const currentPageData = this.pageData[currentPageIndex];

    const containerClassNames = [
      'App',
      currentPageData.sectionSlug,
      (isBrowser || isTablet) ? 'Desktop' : 'Mobile',
      this.state.showNav ? 'nav-visible' : '',
    ];

    const contentSectionDisplay = this.state.showNav && !(isBrowser || isTablet)
      ? 'none'
      : 'block';

    return (
      <div className={containerClassNames.join(' ')}>
        <MobileOnlyView>
          {this.renderNavigation()}
        </MobileOnlyView>
        <section className="container" style={{ display: contentSectionDisplay }}>
          <BrowserView>
            {this.renderNavigation()}
          </BrowserView>
          <TabletView>
            {this.renderNavigation()}
          </TabletView>
          <Header
            sectionSlug={currentPageData.sectionSlug}
            section={currentPageData.section}
            template={currentPageData.template}
            onToggleNavigation={this.handleToggleNavVisibility}
          />
          {this.pageData.map((pageData, index) => (
            <Page
              key={index}
              {...pageData}
              onPageChange={this.handlePageChange}
              invisible={index !== currentPageIndex}
            />
          ))}
          <BrowserView>
            <SelectedState template={currentPageIndex.template} />
          </BrowserView>
          <TabletView>
            <SelectedState template={currentPageIndex.template} />
          </TabletView>
          <Footer
            section={currentPageData.section}
            footer={currentPageData.footer}
          />
        </section>
      </div>
    );
  }
}

class Footer extends Component {
  static propTypes = {
    section: PropTypes.string,
    footer: PropTypes.string,
  }

  render() {
    if (this.props.section !== ACTIONS) {
      return null;
    }

    return (
      <footer>
        {this.props.footer}
      </footer>
    );
  }
}
